/**
 * CustomerAccountLayout — premium SaaS-style shell for the customer account
 * center. Sidebar on lg+, hamburger drawer on mobile. Sections:
 *   - Dashboard (home summary)
 *   - My Rewards
 *   - Favorites
 *   - Orders
 *   - Referrals
 *   - Notifications (with unread bell badge)
 *   - Settings
 *
 * Auth: requires `nofee_customer_token` in localStorage. Otherwise redirects
 * to /rewards?auth=login.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard,
  Gift,
  Heart,
  ShoppingBag,
  Users,
  Bell,
  FileText,
  Settings as SettingsIcon,
  LogOut,
  Menu as MenuIcon,
  X,
  ChevronRight,
} from 'lucide-react';
import { NofeeNavLogo } from './NoFeeLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ACCOUNT_NAV = [
  { key: 'dashboard', path: '/account',                     label: 'Dashboard',  Icon: LayoutDashboard },
  { key: 'rewards',   path: '/account/rewards',             label: 'My Rewards', Icon: Gift },
  { key: 'favorites', path: '/account/favorites',           label: 'Favorites',  Icon: Heart },
  { key: 'orders',    path: '/account/orders',              label: 'Orders',     Icon: ShoppingBag },
  { key: 'referrals', path: '/account/referrals',           label: 'Referrals',  Icon: Users },
  // iter87 (Jun 2026): "Statement" lifted to its own sidebar entry
  // (was a mid-page card before). Customers couldn't see the View Full
  // Statement CTA in the middle of /account/rewards — left-rail
  // placement is way easier to scan.
  { key: 'statement', path: '/account/rewards/statement',   label: 'Statement',  Icon: FileText },
  { key: 'settings',  path: '/account/settings',            label: 'Settings',   Icon: SettingsIcon },
];

const CustomerAccountLayout = ({ children, currentKey, title, subtitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [me, setMe] = useState(null);
  const [bellOpen, setBellOpen] = useState(false);

  // Auth gate
  useEffect(() => {
    if (!token) {
      navigate('/rewards?auth=login', { replace: true });
    }
  }, [token, navigate]);

  // Fetch profile snapshot (used for header chip)
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/customers/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMe(r.data?.customer || null))
      .catch(() => {});
  }, [token]);

  // Poll unread badge every 60s
  const refreshUnread = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/customer/account/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnread(r.data?.unread_count || 0);
    } catch {
      /* ignore */
    }
  }, [token]);
  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 60000);
    return () => clearInterval(t);
  }, [refreshUnread, location.pathname]);

  const onLogout = () => {
    try { localStorage.removeItem('nofee_customer_token'); } catch {}
    navigate('/rewards', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="customer-account-layout">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/85"
              data-testid="account-mobile-menu-btn"
              aria-label="Open menu"
            >
              <MenuIcon className="w-5 h-5" />
            </button>
            <Link to="/" data-testid="account-logo">
              <NofeeNavLogo height={36} />
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {/* iter86 (Jun 2026): bell now opens a small placeholder
                popover instead of routing to a dedicated page. Order
                activity / referral activity / balance changes already
                live in dedicated sections under My Account; the bell is
                kept for future broadcasts (promos, restaurant news,
                reward alerts). */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setBellOpen((v) => !v)}
                className="relative w-10 h-10 inline-flex items-center justify-center rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/85 hover:text-yellow-300 transition-colors"
                data-testid="account-bell-btn"
                aria-label="Notifications"
                aria-expanded={bellOpen}
              >
                <Bell className="w-4.5 h-4.5" />
                {unread > 0 && (
                  <span
                    data-testid="account-bell-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-extrabold flex items-center justify-center"
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </button>
              {bellOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[110]"
                    onClick={() => setBellOpen(false)}
                    aria-hidden="true"
                  />
                  <div
                    data-testid="account-bell-popover"
                    className="absolute right-0 mt-2 w-80 p-4 rounded-2xl border border-yellow-500/25 bg-zinc-950 shadow-2xl z-[120]"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Bell className="w-4 h-4 text-yellow-300" />
                      <h3 className="text-[13px] font-extrabold text-white tracking-tight">You&apos;re all caught up</h3>
                    </div>
                    <p className="text-[12.5px] text-white/65 leading-relaxed">
                      Future order updates, promotions, restaurant news, and reward alerts will appear here.
                    </p>
                    <p className="text-[11.5px] text-white/45 leading-relaxed mt-2">
                      Looking for your reward history? See{' '}
                      <Link to="/account/rewards/statement" onClick={() => setBellOpen(false)} className="text-yellow-300 underline">
                        My Rewards → Statement
                      </Link>.
                    </p>
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 hover:border-rose-500/40 text-white/65 hover:text-rose-300 text-[12.5px] font-bold transition-all"
              data-testid="account-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-7 grid lg:grid-cols-[260px_1fr] gap-6 lg:gap-8">
        {/* Sidebar — desktop only */}
        <aside className="hidden lg:block sticky top-[88px] self-start" data-testid="account-sidebar">
          {me && (
            <div className="mb-5 p-4 rounded-2xl border border-white/8 bg-white/[0.02]">
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-white/45 font-bold">
                Signed in
              </div>
              <div className="mt-1 text-[14px] font-semibold text-white truncate">{me.name}</div>
              <div className="text-[12px] text-white/55 truncate">{me.email}</div>
            </div>
          )}
          <nav className="space-y-1">
            {ACCOUNT_NAV.map((it) => {
              const active = currentKey === it.key;
              return (
                <Link
                  key={it.key}
                  to={it.path}
                  data-testid={`account-nav-${it.key}`}
                  className={`group flex items-center justify-between px-3 py-2.5 rounded-xl transition-all ${
                    active
                      ? 'bg-yellow-500/[0.08] border border-yellow-500/40 text-yellow-200'
                      : 'border border-transparent text-white/75 hover:text-yellow-200 hover:bg-white/[0.03]'
                  }`}
                  aria-current={active ? 'page' : undefined}
                >
                  <span className="flex items-center gap-2.5">
                    <it.Icon className={`w-4 h-4 ${active ? 'text-yellow-300' : 'text-white/55 group-hover:text-yellow-300/85'}`} />
                    <span className="text-[14px] font-semibold">{it.label}</span>
                  </span>
                  {it.key === 'notifications' && unread > 0 ? (
                    <span className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-extrabold inline-flex items-center justify-center">
                      {unread > 99 ? '99+' : unread}
                    </span>
                  ) : (
                    <ChevronRight className={`w-3.5 h-3.5 ${active ? 'text-yellow-300' : 'text-white/25'}`} />
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main data-testid="account-main">
          {(title || subtitle) && (
            <div className="mb-6 sm:mb-7">
              {title && (
                <h1
                  data-testid="account-page-title"
                  className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.03em] text-white"
                >
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="mt-2 text-[14px] sm:text-[15px] text-white/55 leading-relaxed">
                  {subtitle}
                </p>
              )}
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[80]" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            className="absolute top-0 left-0 h-full w-[88%] max-w-[360px] bg-gradient-to-b from-zinc-950 via-black to-zinc-950 border-r border-yellow-900/30 flex flex-col"
            data-testid="account-mobile-drawer"
            style={{ animation: 'nofee-account-drawer-slide 280ms cubic-bezier(0.22,1,0.36,1) both' }}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/[0.06]">
              <NofeeNavLogo height={36} />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-white/[0.04] border border-white/10 text-white/80"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {me && (
              <div className="px-5 pt-4">
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-white/45 font-bold">
                  Signed in
                </div>
                <div className="mt-1 text-[14px] font-semibold text-white truncate">{me.name}</div>
                <div className="text-[12px] text-white/55 truncate">{me.email}</div>
              </div>
            )}
            <nav className="flex-1 overflow-y-auto px-3 pt-5 pb-4 space-y-1">
              {ACCOUNT_NAV.map((it) => {
                const active = currentKey === it.key;
                return (
                  <Link
                    key={it.key}
                    to={it.path}
                    onClick={() => setDrawerOpen(false)}
                    data-testid={`account-mobile-nav-${it.key}`}
                    className={`flex items-center justify-between px-3 py-3.5 rounded-xl transition-all ${
                      active
                        ? 'bg-yellow-500/[0.08] border border-yellow-500/40 text-yellow-200'
                        : 'border border-transparent text-white/80 hover:bg-white/[0.03]'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="flex items-center gap-3">
                      <it.Icon className={`w-4 h-4 ${active ? 'text-yellow-300' : 'text-white/55'}`} />
                      <span className="text-[14px] font-semibold">{it.label}</span>
                    </span>
                    {it.key === 'notifications' && unread > 0 && (
                      <span className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-extrabold inline-flex items-center justify-center">
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
            <div className="px-5 pb-6 pt-3 border-t border-white/[0.06]">
              <button
                type="button"
                onClick={onLogout}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/12 hover:border-rose-500/40 text-white/70 hover:text-rose-300 text-[13px] font-bold transition-all whitespace-nowrap"
                data-testid="account-mobile-logout-btn"
              >
                <LogOut className="w-4 h-4 shrink-0" />
                <span className="truncate">Sign out</span>
              </button>
            </div>
          </aside>
          <style>{`
            @keyframes nofee-account-drawer-slide {
              0% { transform: translateX(-100%); opacity: 0.6; }
              100% { transform: translateX(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default CustomerAccountLayout;
